/**
 * @file: useFeedThreads.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Inbox thread loader with split fetch semantics:
 *
 *   1. `pinnedThreads` — one fetch for the "virtually pinned" scopes
 *      (system, rewards, maintenance-plans). Loaded in full once;
 *      these scopes typically have ≤1 thread per user, so a single
 *      call is plenty.
 *   2. `otherThreads`  — paginated stream of every OTHER scope (today
 *      that means order threads). Drives infinite scroll.
 *
 * Both queries fire concurrently on mount. The screen renders pinned
 * first then others, so the important scopes are guaranteed to appear
 * even if the paginated query is still loading or empty.
 */

import { FeedThreadInboxItem, FeedThreadScope } from '@euler/model/feed';
import { useServiceFactory } from '@euler/services/factory';
import { usePersistFn } from '@euler/utils/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';

export const kPinnedScopes: FeedThreadScope[] = [
  FeedThreadScope.System,
  FeedThreadScope.Rewards,
  FeedThreadScope.MaintenancePlans,
  FeedThreadScope.Leads,
  FeedThreadScope.MarketingFollowup,
];

const kOtherPageSize = 30;
// Generous — should easily fit every pinned-by-scope thread in one shot
// (≤3 today, since each scope is per-user singleton-ish).
const kPinnedPageSize = 50;

export interface UseFeedThreadsResult {
  /** Sticky-top section. One fetch, no pagination. */
  pinnedThreads: FeedThreadInboxItem[];
  /** Paginated tail (today: order threads). */
  otherThreads: FeedThreadInboxItem[];

  /** Both queries are still on their first round-trip. */
  loading: boolean;
  refreshing: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: Error | undefined;

  /**
   * Reload both streams from the server. Pass `{ silent: true }` to skip
   * flipping the `refreshing` flag — keeps the pull-to-refresh indicator
   * off for programmatic reloads (mount, focus flip, mark-all-read echo)
   * while leaving manual user pulls visible.
   */
  refresh: (options?: { silent?: boolean }) => Promise<void>;
  loadMore: () => Promise<void>;
  /** Patch any thread (pinned or other) in place — no network call. */
  patchThread: (id: string, patch: Partial<FeedThreadInboxItem>) => void;
  /** Drop a thread locally (after delete or archive). */
  removeThread: (id: string) => void;
}

export const useFeedThreads = (): UseFeedThreadsResult => {
  const { feedService } = useServiceFactory();

  const [pinnedThreads, setPinnedThreads] = useState<FeedThreadInboxItem[]>([]);
  const [otherThreads, setOtherThreads] = useState<FeedThreadInboxItem[]>([]);
  const [pinnedLoading, setPinnedLoading] = useState(true);
  const [othersLoading, setOthersLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState<Error | undefined>(undefined);
  // Per-stream req counters. A shared counter would cancel one stream
  // when the other increments it (the pinned + others fetches run in
  // parallel and a single counter makes pinned's stale-check fail as
  // soon as others ticks the ref).
  const pinnedReqRef = useRef(0);
  const othersReqRef = useRef(0);

  const fetchPinnedPage = useCallback(async () => {
    const reqId = ++pinnedReqRef.current;
    const res = await feedService.listThreads({
      scope: kPinnedScopes,
      limit: kPinnedPageSize,
    });
    if (reqId !== pinnedReqRef.current) return null;
    return res;
  }, [feedService]);

  const fetchOthersPage = useCallback(
    async (cursor: string | undefined) => {
      const reqId = ++othersReqRef.current;
      const res = await feedService.listThreads({
        excludeScopes: kPinnedScopes,
        cursor,
        limit: kOtherPageSize,
      });
      if (reqId !== othersReqRef.current) return null;
      return res;
    },
    [feedService],
  );

  const refresh = usePersistFn(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    if (!silent) setRefreshing(true);
    setError(undefined);
    try {
      const [pinned, others] = await Promise.all([
        fetchPinnedPage(),
        fetchOthersPage(undefined),
      ]);
      if (pinned) setPinnedThreads(pinned.items);
      if (others) {
        setOtherThreads(others.items);
        setNextCursor(others.nextCursor);
      }
    } catch (e) {
      setError(e as Error);
    } finally {
      if (!silent) setRefreshing(false);
      setPinnedLoading(false);
      setOthersLoading(false);
    }
  });

  const loadMore = usePersistFn(async () => {
    if (loadingMore || nextCursor == null) return;
    setLoadingMore(true);
    try {
      const res = await fetchOthersPage(nextCursor);
      if (res) {
        setOtherThreads(prev => [...prev, ...res.items]);
        setNextCursor(res.nextCursor);
      }
    } catch (e) {
      setError(e as Error);
    } finally {
      setLoadingMore(false);
    }
  });

  // Initial load is silent — the centered ActivityIndicator gated on
  // `loading` already covers the empty-list spinner; pull-to-refresh is
  // for user-driven reloads only.
  useEffect(() => {
    void refresh({ silent: true });
  }, [refresh]);

  const patchThread = useCallback(
    (id: string, patch: Partial<FeedThreadInboxItem>) => {
      setPinnedThreads(prev =>
        prev.map(t => (t.id === id ? { ...t, ...patch } : t)),
      );
      setOtherThreads(prev =>
        prev.map(t => (t.id === id ? { ...t, ...patch } : t)),
      );
    },
    [],
  );
  const removeThread = useCallback((id: string) => {
    setPinnedThreads(prev => prev.filter(t => t.id !== id));
    setOtherThreads(prev => prev.filter(t => t.id !== id));
  }, []);

  return {
    pinnedThreads,
    otherThreads,
    loading: pinnedLoading || othersLoading,
    refreshing,
    loadingMore,
    hasMore: nextCursor != null,
    error,
    refresh,
    loadMore,
    patchThread,
    removeThread,
  };
};
